// lib/access-suspension.ts
//
// Billing suspension for an organisation: the account can still look at and download
// everything it owns, but it cannot spend anything, no agent turns, no generations.
//
// The single source of truth for BOTH the notice the user reads and the refusals the
// server issues. Keeping them apart is how you end up telling someone access is limited
// and then letting the spend through, or the reverse: a silent failure with no reason.

import { QWEN_IMAGE_21_MODEL_ID, shouldUseLocalQwen21 } from '#lib/qwen-image-21';

/** URW's billing suspension was removed in full at the owner's request on 2026-09-14. */
export const SUSPENDED_ORGANISATION_IDS: ReadonlySet<string> = new Set<string>();

/**
 * Teams inside a suspended organisation whose access is restored, each mapped to the model
 * families it still cannot spend on. An empty list means that team is back to full access.
 *
 * Keyed by team, not by user list, so the organisation moving people in or out of a team
 * changes their access with no deploy. The values are model id prefixes, so the next member of
 * a withheld family is withheld the day it ships rather than the day someone remembers.
 */
export const SUSPENSION_EXEMPT_TEAMS: ReadonlyMap<string, readonly string[]> = new Map<string, readonly string[]>();

/**
 * When the restriction begins. Armed immediately on 2026-08-15 at the owner's request so the
 * behaviour could be exercised on a real account; it was originally scheduled for Monday
 * 2026-08-17, midnight Paris (Date.parse('2026-08-17T00:00:00+02:00')). Kept as a constant
 * rather than deleted: this is the one line to edit to schedule a future suspension, and the
 * tests are written relative to it.
 */
export const SUSPENSION_STARTS_AT_MS = 0;

/**
 * The one wording the user ever sees, in the notice and on every refusal. Deliberately
 * says nothing about which feature was refused: the reason is the account, not the click.
 */
export const SUSPENSION_MESSAGE =
  'Because of unpaid invoices, access to the app is currently limited. '
  + 'Full access will be restored once the outstanding invoices are settled. '
  + 'For any question, please contact your account manager.';

/**
 * The organisation half of the question: on the list and past the start instant. Read the clock
 * at call time, never at module load: a server process started before Monday would otherwise
 * never arm.
 */
const isSuspensionArmed = (organisationId: string | null | undefined, now: number): boolean => {
  if (!organisationId) return false;
  if (now < SUSPENSION_STARTS_AT_MS) return false;
  return SUSPENDED_ORGANISATION_IDS.has(organisationId);
};

/**
 * `now` is injectable so the server can pass its own clock and tests can pin one; callers
 * that omit it get the current time.
 */
export const isOrganisationSuspended = (
  organisationId: string | null | undefined,
  now: number = Date.now(),
  teamId?: string | null,
): boolean => {
  if (!isSuspensionArmed(organisationId, now)) return false;
  return !(teamId && SUSPENSION_EXEMPT_TEAMS.has(teamId));
};

/**
 * The model families this caller cannot spend on, empty when nothing is withheld. The model
 * picker needs the same answer as the server, or it offers a model the server then refuses.
 *
 * A team with no carve-out gets an empty list: nothing is *withheld* from it because everything
 * is already refused by `isOrganisationSuspended`. Never read this on its own to decide a spend,
 * ask `isModelSuspended`.
 */
export const withheldModelFamilies = (
  organisationId: string | null | undefined,
  teamId: string | null | undefined,
  now: number = Date.now(),
): readonly string[] => {
  if (!isSuspensionArmed(organisationId, now)) return [];
  if (!teamId) return [];
  return SUSPENSION_EXEMPT_TEAMS.get(teamId) ?? [];
};

/**
 * Prefix match on the model id, and on its leaf so a provider-prefixed runtime name
 * ('bytedance/seedance-2.0') is caught as well. A deny gate has to fail closed.
 */
export const isModelInFamilies = (
  modelId: string | null | undefined,
  families: readonly string[],
): boolean => {
  if (!modelId || families.length === 0) return false;
  const id = modelId.trim().toLowerCase();
  if (!id) return false;
  const leaf = id.split('/').pop() || id;
  return families.some((family) => id.startsWith(family) || leaf.startsWith(family));
};

/**
 * The whole verdict a client needs, in one object; its withheld families also carry the models
 * this server cannot run (`unrunnableModelFamilies`). Resolved on the SERVER: a tab that has been
 * open since before the last deploy still holds the tables that shipped with its bundle, so it
 * would keep refusing a team whose access was restored ten minutes ago, until someone thought to
 * reload the page. Measured on 2026-08-24: a restored user opened ten sessions and sent nothing.
 */
export type AccessSuspensionVerdict = {
  suspended: boolean;
  withheldModelFamilies: readonly string[];
};

/** Models that exist only on a local runtime, so a server without that runtime cannot run them. */
export const LOCAL_ONLY_MODEL_FAMILIES: readonly string[] = [QWEN_IMAGE_21_MODEL_ID];

/**
 * Local-only models this server cannot run, whatever the account. They ride the verdict so the
 * picker, the agent's list and SELECT_MODEL hide them through the same channel. They stay out of
 * `isModelSuspended`: a spend on them fails with the route's own explicit error, not the unpaid
 * invoices wording.
 */
/** Models reached on a provider whose key a server may not hold: QuiverAI's Arrow 2 family. */
export const KEY_GATED_MODEL_FAMILIES: readonly string[] = ['arrow-2'];
/** Every family a server may be unable to run; a verdict's withheld entry among them is hidden outright. */
export const SERVER_GATED_MODEL_FAMILIES: readonly string[] = [...LOCAL_ONLY_MODEL_FAMILIES, ...KEY_GATED_MODEL_FAMILIES];

export const unrunnableModelFamilies = (): readonly string[] => [
  ...(shouldUseLocalQwen21() ? [] : LOCAL_ONLY_MODEL_FAMILIES),
  ...(process.env.QUIVERAI_API_KEY?.trim() ? [] : KEY_GATED_MODEL_FAMILIES),
];

/** A verdict's withheld model that no setting can bring back: lists hide it outright instead of offering it greyed out. */
export const isWithheldAsUnrunnable = (modelId: string, withheldFamilies: readonly string[]): boolean => (
  isModelInFamilies(modelId, SERVER_GATED_MODEL_FAMILIES) && isModelInFamilies(modelId, withheldFamilies)
);

export const resolveAccessSuspension = (
  organisationId: string | null | undefined,
  teamId: string | null | undefined,
  now: number = Date.now(),
): AccessSuspensionVerdict => ({
  suspended: isOrganisationSuspended(organisationId, now, teamId),
  withheldModelFamilies: [...withheldModelFamilies(organisationId, teamId, now), ...unrunnableModelFamilies()],
});

/**
 * What a client component should read: the server's verdict once it has arrived, this bundle's
 * own tables until then. Here rather than in each of the four call sites, because they all have
 * to make the same choice and one of them getting it wrong is a silently blocked user.
 */
export const clientAccessSuspension = (
  serverVerdict: AccessSuspensionVerdict | null | undefined,
  organisationId: string | null | undefined,
  teamId: string | null | undefined,
  now: number = Date.now(),
): AccessSuspensionVerdict => serverVerdict ?? resolveAccessSuspension(organisationId, teamId, now);

/**
 * The one question a spend gate asks: may this account run this model right now. True when the
 * whole account is suspended, and true when the account is back but the model belongs to a
 * family its team still cannot spend on.
 */
export const isModelSuspended = (input: {
  organisationId: string | null | undefined;
  teamId: string | null | undefined;
  modelId: string | null | undefined;
  now?: number;
}): boolean => {
  const now = input.now ?? Date.now();
  if (isOrganisationSuspended(input.organisationId, now, input.teamId)) return true;
  return isModelInFamilies(input.modelId, withheldModelFamilies(input.organisationId, input.teamId, now));
};
